import { useState, useMemo, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Send, Info, Plus, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

interface CostFieldConfig {
  key: string;
  label: string;
  icon: string;
  hint?: string | null;
}

interface QuoteConfigData {
  cost_fields: CostFieldConfig[];
  show_profit: boolean;
  show_vat: boolean;
  unit_label: string;
}

export interface DynamicQuoteSentData {
  totalPrice: number;
  otherCosts: { label: string; value: number }[];
  profit: number | null;
  vatRate: number | null;
  area: number;
}

interface Props {
  config: QuoteConfigData;
  supplierId: string;
  inquiryId: string;
  inquiryRegion: string | null;
  area?: number;
  onQuoteSent: (data: DynamicQuoteSentData) => void;
}

interface CostLine {
  id: string;
  label: string;
  icon: string;
  hint?: string | null;
  value: string;
  removable: boolean;
}

let nextId = 1;
const uid = () => `dqf_${nextId++}`;

export function DynamicQuoteForm({ config, supplierId, inquiryId, inquiryRegion, area = 0, onQuoteSent }: Props) {
  const initialCosts: CostLine[] = config.cost_fields.map(f => ({
    id: uid(),
    label: f.label,
    icon: f.icon,
    hint: f.hint,
    value: "",
    removable: false,
  }));

  const [costs, setCosts] = useState<CostLine[]>(initialCosts);
  const [marginAmount, setMarginAmount] = useState("");
  const [disclaimerChecked, setDisclaimerChecked] = useState(true);
  const [vatRate, setVatRate] = useState(12);
  const [newCostLabel, setNewCostLabel] = useState("");
  const [showAddCost, setShowAddCost] = useState(false);
  const [saving, setSaving] = useState(false);

  const updateCost = useCallback((id: string, value: string) => {
    setCosts(prev => prev.map(c => c.id === id ? { ...c, value } : c));
  }, []);

  const removeCost = useCallback((id: string) => {
    setCosts(prev => prev.filter(c => c.id !== id));
  }, []);

  const addCustomCost = () => {
    const label = newCostLabel.trim();
    if (!label) return;
    setCosts(prev => [...prev, { id: uid(), label, icon: "📦", value: "", removable: true }]);
    setNewCostLabel("");
    setShowAddCost(false);
  };

  const totalCost = useMemo(
    () => costs.reduce((sum, c) => sum + (parseFloat(c.value) || 0), 0),
    [costs],
  );

  const margin = parseFloat(marginAmount) || 0;
  const showProfit = config.show_profit;
  const showVat = config.show_vat;

  // If profit is shown: selling = cost + margin. If not: selling = totalCost
  const sellingPrice = showProfit
    ? (totalCost > 0 && margin > 0 ? Math.round(totalCost + margin) : 0)
    : (totalCost > 0 ? Math.round(totalCost) : 0);

  const marginPct = totalCost > 0 && margin > 0 ? Math.round((margin / totalCost) * 100 * 10) / 10 : 0;
  const pricePerUnit = area > 0 && sellingPrice > 0 ? Math.round(sellingPrice / area) : 0;
  const costPerUnit = area > 0 && totalCost > 0 ? Math.round(totalCost / area) : 0;
  const priceWithVat = showVat ? Math.round(sellingPrice * (1 + vatRate / 100)) : sellingPrice;

  const canSubmit = showProfit
    ? (totalCost > 0 && margin > 0 && sellingPrice > 0)
    : (totalCost > 0);

  const disclaimerNote = disclaimerChecked ? "Přesná cena bude stanovena po zaměření stavby." : "";

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);

    try {
      const otherCosts = costs
        .filter(c => c.value)
        .map(c => ({ label: c.label, value: parseFloat(c.value) || 0 }));

      const insertData: Record<string, unknown> = {
        supplier_id: supplierId,
        inquiry_id: inquiryId,
        total_price: sellingPrice,
        region: inquiryRegion || null,
        notes: disclaimerNote || null,
        other_costs: otherCosts,
        profit: showProfit ? (margin > 0 ? margin : null) : null,
        vat_rate: showVat ? vatRate : null,
      };

      if (area > 0) {
        insertData.area_m2 = area;
        insertData.price_per_m2 = pricePerUnit;
      }

      const { error } = await supabase
        .from("supplier_quotes")
        .insert(insertData as any);

      if (error) {
        if (error.code === "23505") {
          toast.error("Na tuto poptávku jste již nabídku odeslal");
        } else {
          throw error;
        }
        return;
      }

      // Notify customer
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-customer-quote`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
            body: JSON.stringify({ inquiry_id: inquiryId, total_price: sellingPrice }),
          });
        }
      } catch (e) {
        console.error("Failed to notify customer:", e);
      }

      toast.success("Nabídka odeslána zákazníkovi!");
      onQuoteSent({
        totalPrice: sellingPrice,
        otherCosts,
        profit: showProfit ? (margin > 0 ? margin : null) : null,
        vatRate: showVat ? vatRate : null,
        area,
      });
    } catch (err) {
      console.error(err);
      toast.error("Nepodařilo se odeslat nabídku");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-border rounded-xl bg-card overflow-hidden">
      {/* Header */}
      <div className="px-5 pt-5 pb-3">
        <h3 className="font-semibold text-foreground text-sm flex items-center gap-2">
          📊 Kalkulace nabídky
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          Vyplňte náklady{showProfit ? " a zisk" : ""} – zákazníkovi se odešle pouze výsledná cena.
        </p>
      </div>

      {/* ── Step 1: Costs ── */}
      <div className="mx-5 mb-4 border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border bg-muted/30">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
            <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center">1</span>
            Náklady <span className="normal-case font-normal">bez DPH</span>
          </span>
        </div>

        <div className="px-4 py-4 space-y-3">
          {costs.map((cost) => (
            <div key={cost.id}>
              <div className="flex items-center gap-2">
                <span className="text-base w-7 text-center shrink-0">{cost.icon}</span>
                <span className="text-sm text-foreground w-28 shrink-0 truncate">{cost.label}</span>
                <div className="relative flex-1">
                  <input
                    type="number"
                    min="0"
                    step="100"
                    value={cost.value}
                    onChange={(e) => updateCost(cost.id, e.target.value)}
                    placeholder="0"
                    className="w-full px-3 py-2 pr-10 rounded-lg border border-border bg-background text-foreground text-sm font-medium text-right outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground/30"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">Kč</span>
                </div>
                {cost.removable && (
                  <button
                    type="button"
                    onClick={() => removeCost(cost.id)}
                    className="p-1.5 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              {cost.hint && (
                <p className="text-[10px] text-muted-foreground ml-9 mt-0.5">{cost.hint}</p>
              )}
            </div>
          ))}

          {/* Add custom cost */}
          {showAddCost ? (
            <div className="flex items-center gap-2 pt-1">
              <span className="text-base w-7 text-center shrink-0">📦</span>
              <input
                type="text"
                value={newCostLabel}
                onChange={(e) => setNewCostLabel(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addCustomCost()}
                placeholder="Název nákladu..."
                autoFocus
                className="w-28 shrink-0 px-2 py-1.5 rounded-lg border border-border bg-background text-foreground text-sm outline-none focus:ring-2 focus:ring-ring"
              />
              <button
                type="button"
                onClick={addCustomCost}
                disabled={!newCostLabel.trim()}
                className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium disabled:opacity-50"
              >
                Přidat
              </button>
              <button
                type="button"
                onClick={() => { setShowAddCost(false); setNewCostLabel(""); }}
                className="px-2 py-1.5 rounded-lg text-xs text-muted-foreground hover:text-foreground"
              >
                Zrušit
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowAddCost(true)}
              className="flex items-center gap-1.5 text-xs text-primary font-medium hover:text-primary/80 transition-colors pt-1"
            >
              <Plus className="w-3.5 h-3.5" />
              Přidat další náklad
            </button>
          )}

          {/* Cost subtotal */}
          <div className="flex items-center justify-between border-t border-border pt-3">
            <span className="text-sm font-semibold text-foreground">Náklady celkem</span>
            <div className="text-right">
              <span className="text-sm font-bold text-foreground">{totalCost.toLocaleString("cs-CZ")} Kč</span>
              {area > 0 && totalCost > 0 && (
                <p className="text-[10px] text-muted-foreground">{costPerUnit} Kč/{config.unit_label}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Step 2: Profit (optional) ── */}
      {showProfit && (
        <div className="mx-5 mb-4 border border-border rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-muted/30">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
              <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center">2</span>
              Zisk <span className="normal-case font-normal">bez DPH</span>
            </span>
          </div>

          <div className="px-4 py-4 space-y-3">
            <div className="relative">
              <input
                type="number"
                min="0"
                step="1000"
                value={marginAmount}
                onChange={(e) => setMarginAmount(e.target.value)}
                placeholder="např. 50000"
                className="w-full px-3 py-3 pr-10 rounded-lg border border-border bg-background text-foreground text-lg font-semibold text-right outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground/30"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">Kč</span>
            </div>

            {totalCost > 0 && margin > 0 && (
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-muted/30 rounded-lg p-2.5 text-center">
                  <p className="text-[10px] text-muted-foreground uppercase font-medium">Marže</p>
                  <p className="text-sm font-bold text-primary">{marginPct} %</p>
                </div>
                {area > 0 && (
                  <div className="bg-muted/30 rounded-lg p-2.5 text-center">
                    <p className="text-[10px] text-muted-foreground uppercase font-medium">Cena/{config.unit_label}</p>
                    <p className="text-sm font-bold text-foreground">{pricePerUnit > 0 ? `${pricePerUnit} Kč` : "—"}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Step 3: Result ── */}
      <div className={`mx-5 mb-4 border rounded-xl overflow-hidden ${canSubmit ? "border-primary/30 bg-primary/5" : "border-border bg-muted/20"}`}>
        <div className={`px-4 py-3 border-b ${canSubmit ? "border-primary/20" : "border-border"}`}>
          <span className={`text-xs font-semibold uppercase tracking-wide flex items-center gap-1.5 ${canSubmit ? "text-primary" : "text-muted-foreground"}`}>
            <span className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center ${canSubmit ? "bg-primary text-primary-foreground" : "bg-muted-foreground/20 text-muted-foreground"}`}>
              {showProfit ? "3" : "2"}
            </span>
            Výsledná cena pro zákazníka
          </span>
        </div>

        <div className="px-4 py-4 space-y-3">
          <div className="text-center py-2">
            <p className={`text-3xl font-bold ${sellingPrice > 0 ? "text-foreground" : "text-muted-foreground/40"}`}>
              {sellingPrice.toLocaleString("cs-CZ")} Kč {showVat && <span className="text-base font-normal text-muted-foreground">bez DPH</span>}
            </p>
            {area > 0 && pricePerUnit > 0 && (
              <p className="text-sm text-muted-foreground mt-0.5">{pricePerUnit} Kč/{config.unit_label}</p>
            )}
          </div>

          {/* VAT selector */}
          {showVat && (
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Sazba DPH</label>
              <div className="flex gap-2">
                {[12, 21].map(rate => (
                  <button
                    key={rate}
                    type="button"
                    onClick={() => setVatRate(rate)}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      vatRate === rate
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border text-muted-foreground hover:bg-accent"
                    }`}
                  >
                    {rate} %
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                12 % – sociální bydlení, rodinné domy do 350 m² · 21 % – ostatní
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                S DPH: <span className="font-semibold text-foreground">{priceWithVat.toLocaleString("cs-CZ")} Kč</span>
              </p>
            </div>
          )}

          {/* Disclaimer */}
          <label className="flex items-start gap-2.5 cursor-pointer group">
            <input
              type="checkbox"
              checked={disclaimerChecked}
              onChange={(e) => setDisclaimerChecked(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-border accent-primary cursor-pointer"
            />
            <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors leading-relaxed">
              <Info className="w-3 h-3 inline-block mr-1 -mt-0.5" />
              Informovat zákazníka, že přesná cena bude stanovena až po zaměření
            </span>
          </label>

          {/* Privacy */}
          {showProfit && (
            <div className="flex items-start gap-3 bg-primary/10 border border-primary/20 rounded-xl px-4 py-3">
              <ShieldCheck className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-foreground">Vaše náklady a zisk zůstanou soukromé.</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Zákazník uvidí pouze výslednou cenu <span className="font-semibold text-foreground">{sellingPrice.toLocaleString("cs-CZ")} Kč</span>.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Submit */}
      <div className="p-5 pt-0">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={saving || !canSubmit}
          className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-primary-foreground rounded-xl text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {saving ? (
            <span className="animate-pulse">Odesílám...</span>
          ) : (
            <>
              <Send className="w-4 h-4" />
              Odeslat nabídku{canSubmit ? ` · ${sellingPrice.toLocaleString("cs-CZ")} Kč` : ""}
            </>
          )}
        </button>
        {!canSubmit && (
          <p className="text-[10px] text-muted-foreground text-center mt-2">
            Vyplňte náklady{showProfit ? " a zisk" : ""} pro výpočet ceny
          </p>
        )}
        {canSubmit && showVat && (
          <p className="text-[10px] text-muted-foreground text-center mt-2">
            s DPH {priceWithVat.toLocaleString("cs-CZ")} Kč
          </p>
        )}
      </div>
    </div>
  );
}
